/* eslint-disable complexity */
import { findApplicant } from '@hc-frontend/business'
import { Gender } from '@hc-frontend/entities'
import Templater from '@hc-frontend/shared-assets/src/js/utils/dom/Templater'
import { getISODate } from '@hc-frontend/utils'
import { MAX_PLANS_TO_COMPARE_NEW } from 'core/constants/compareNumberPlans'
import { IS_AGENT } from 'core/constants/environment'
import {
  CHOICE,
  CORE,
  DELUXE,
  DENTAL,
  ECONOMY,
  EPIC,
  LATITUDE,
  QUANTUM,
  STANDARD,
  SURE_CARE
} from 'core/constants/products'
import saveThisQuoteConfig from 'core/controllerPlansFixed/saveThisQuoteConfig'
import initLoadingProgress from 'core/controllers/initLoadingProgress'
import getProductsWithCoverageFlag from 'core/DOM/getProductsWithCoverageFlag'
import renderHeaderProducts from 'core/DOM/renderHeaderProducts'
import renderPreOpBanner from 'core/DOM/renderPreopBanner'
import setScrollToHandler from 'core/DOM/setScrollToHandler'
import tooltip from 'core/DOM/tooltip'
import updateFirstNameCopy from 'core/DOM/updateFirstNameCopy'
import initSmartly from 'core/initPage/absmartly'
import { sendQuotesViewEvent } from 'core/services/email/blueshift/implementations/quotes'
import getCoverageDuration from 'core/services/getCoverageDuration'
import { updateSessionContext } from 'core/services/updateApplicantContext'
import compare from 'ENROLL/quotes/components/compare'
import { SIMILAR_PLANS } from 'messages'
import saveThisQuote from 'pug/mixins/saveThisQuote'
import toastNotification from 'pug/toastNotification'
import getAdditionalInfo from 'sites/enroll.pivothealth.com/add-ons/getAdditionalInfo'
import { QUOTES } from 'sites/routes'
import getById from 'utils/DOM/getById'
import getURLParams from 'utils/DOM/getURLParams'
import removeElements from 'utils/DOM/removeElements'
import textContent from 'utils/DOM/textContent'
import toggleClass from 'utils/DOM/toggleClass'
import setMichiganEpicPlanDisclaimer from 'utils/pivot/disclaimer/setMichiganEpicPlanDisclaimer'
import isSalesCenterSubDomain from 'utils/pivot/isSalesCenterSubDomain'
import getURLProfile from 'utils/pivot/member/getURLProfile'
import hasEpicPPO from 'utils/pivot/plan/hasEpicPPO'
import hasSomePlanByType from 'utils/pivot/plan/hasSomePlanByType'
import store from 'utils/store/withMiddleware'
import isArray from 'utils/validation/isArray'
import offerBTM from './components/offerBTM'
import { loadQuotesTour } from './components/quotes-tour'
import loadShoppingTip from './components/shoppingTip/loadShoppingTip'
import config from './config'
import filterAsQueryString from './filterAsQueryString'
import registerSnowplowListeners from './registerSnowplowListeners'
import registryListenersDOM from './registryListenersDOM'
import getApplicantFirstName from 'utils/pivot/member/getApplicantFirstName'
import { enableMultiCarrierPlans } from 'utils/experiments'
import { enableFlexBenefits } from 'utils/experiments/enableFlexBenefits'

const urlParams = getURLParams()
const updateLoadingProgress = initLoadingProgress(2)
updateLoadingProgress()

function hideLoading() {
  const toggleLoading = () => {
    toggleClass(document.body, 'show-loading', false)
    removeElements('#page-loading')
  }

  updateLoadingProgress({}, toggleLoading)
}

async function showNoPlans() {
  toggleClass(document.body, 'noPlans')
  window.dataLayerPushEvent('NO_COVERAGE_LOCATION')
  return import('pug/mixins/noCoverage/coverageAlternatives')
    .then(method => method.default('stm'))
    .then(hideLoading)
}

async function initDOM(state) {
  // window.hc.smartlySDK is used safely because initPage() was executed before
  const smartlySDK = initSmartly()

  let disableBTM = 1
  if (smartlySDK) {
    smartlySDK.track('ph_display_quotes')
    smartlySDK.publish()

    disableBTM = smartlySDK.treatment('PH_BTM_REDIRECT')
  }

  compare.init(MAX_PLANS_TO_COMPARE_NEW)
  compare.renderCompare()
  saveThisQuoteConfig({ config, propertyPlanId: 'instantQuotePlan' })
  sendQuotesViewEvent()

  const hcStorage = state.hcStorage

  // Setup GTM and add userId to dataLayer
  window.dataLayerPushVariable({
    user_id: hcStorage.healthcareUserId
  })

  renderPreOpBanner(
    hcStorage.location.state_acronym,
    'Notice for Virginia residents: Enrollment for short-term health insurance will be suspended Nov. 1, 2022-Jan. 15, 2023.'
  )

  if (state.plansSTM.length === 0) {
    return showNoPlans()
  }

  getAdditionalInfo(state).then(({ hasDentalActive, hasVisionActive }) => {
    store.update({ hasDentalActive, hasVisionActive, showRecommend: true })
  })

  textContent(
    '.js-profile-data',
    `${hcStorage.members[0].gender === Gender.Male ? 'Male' : 'Female'} / ${
      hcStorage.members[0].age
    } years old`
  )

  // Applicant information
  updateFirstNameCopy({ hcStorage })
  textContent('#state', hcStorage.location.city)
  textContent('#gender', hcStorage.members[0].gender === Gender.Male ? 'Male' : 'Female')
  textContent('#age', hcStorage.members[0].age)
  textContent('.numberOfPlans', state.plansSTM.length)

  const hadPreviusPlan = state.removedCarriers.length
  if (hadPreviusPlan) {
    const fname = getApplicantFirstName(hcStorage)
    textContent('.title--quotes', `${fname}, Thank You for Choosing Pivot Health Plans!`)
    textContent(
      '.sub-title',
      `Please note: Product availability may vary based on your state and past carrier coverage. Not all plans may be displayed in your quotes.`
    )
  }

  // Cigna disclaimers
  toggleClass(
    document.body,
    'cigna',
    state.filterOptions.networkProvider.values.includes('cigna')
  )

  // Managing mobile for Enroll
  setScrollToHandler()

  const epicIDs = state.plansByType[EPIC]
  const plansHaveEpic = hasSomePlanByType([EPIC], state.plansByType)

  toggleClass(document.body, 'nric', plansHaveEpic)
  toggleClass(
    document.body,
    'nric-ppo',
    plansHaveEpic && hasEpicPPO(epicIDs, state.plans)
  )

  const hasCLICPlan = hasSomePlanByType(
    [ECONOMY, QUANTUM, DELUXE, STANDARD, CORE, CHOICE],
    state.plansByType
  )

  toggleClass(document.body, 'clic', hasCLICPlan)

  const hasPANAMPlan = hasSomePlanByType([SURE_CARE], state.plansByType)
  toggleClass(document.body, 'sure_care', hasPANAMPlan)

  toggleClass('.js-statistics-show', 'd-none', true)

  if (hcStorage.location.state_acronym === 'NE') {
    toggleClass(document.body, 'ne')
  }

  registryListenersDOM()
  hideLoading()

  if (typeof urlParams.similar !== 'undefined') {
    toastNotification(SIMILAR_PLANS, 5000)
  }

  if (!disableBTM && state.plansBTM && state.plansBTM.length > 0) {
    toggleClass(document.body, 'offer-btm', true)
    if (isSalesCenterSubDomain()) {
      const stmTab = getById<HTMLInputElement>('plans-tabs__check--stm')
      if (stmTab) {
        stmTab.checked = false
      }
      const btmTab = getById<HTMLInputElement>('plans-tabs__check--btm')
      if (btmTab) {
        btmTab.checked = true
      }
    }
    offerBTM(state)
  }

  const applicant = findApplicant(hcStorage.members).age
  if (applicant >= 60) {
    toggleClass(document.body, 'over60', true)
  }

  tooltip.init()

  loadQuotesTour()
  loadShoppingTip()

  const dentalPlans = state.plansByType[DENTAL]
  const latitudePlans = state.plansByType[LATITUDE]

  toggleClass(
    '.disclaimer__dental',
    'd-none',
    !(isArray(dentalPlans) && dentalPlans.length > 0)
  )
  toggleClass(
    '.disclaimer__latitude-large',
    'd-none',
    !(isArray(latitudePlans) && latitudePlans.length > 0) || enableFlexBenefits()
  )
  toggleClass('.disclaimer__flex-benefits', 'd-none', !enableFlexBenefits())

  const isMultiCarrier = urlParams.months && urlParams.months !== '4'

  if (enableMultiCarrierPlans()) {
    toggleClass('#tiles', 'd-none', isMultiCarrier)
    toggleClass('#filters-wrap', 'd-none', isMultiCarrier)
  }

  setMichiganEpicPlanDisclaimer(state, false)
  toggleClass('body', 'pa', hcStorage.location.state_acronym === 'PA')
  updateSessionContext(hcStorage, config)

  registerSnowplowListeners()
  const { error, data } = await getCoverageDuration(
    hcStorage.location.state_acronym,
    getISODate(new Date(hcStorage.sdate))
  )
  const menuTemplate = Templater(getById('header-menu')?.innerText)

  const products = getProductsWithCoverageFlag(
    { error, data },
    hcStorage.externalPartners
  )
  renderHeaderProducts(menuTemplate, products as never[])

  if (!IS_AGENT) {
    saveThisQuote({
      getPath: () =>
        `${QUOTES}?${getURLProfile({
          placement: config.placement
        })}&${filterAsQueryString()}`,
      placement: config.placement,
      tags: [config.tagMaropost],
      defaultEmail: hcStorage.contact.email_address || hcStorage.members[0].email
    })
  }

  if (!enableMultiCarrierPlans()) {
    removeElements('.d-hidden-pmm')
  }

}

export default initDOM
